import currency from '|/helpers/currency';
import { ReviewSummaryResponse } from '|/services/purchase-api/types';

export interface CarvanaCareModalVariants {
  showSocialProof: boolean;
  impactsFinancingTerms: boolean;
}

export interface GapModalProps {
  impactsFinancingTerms: boolean;
  termMonths?: number;
  monthlyDelta: number;
  downDelta: number;
  termDelta: number;
  vehicleModel?: string;
}

export interface CarvanaCareModalDeltas {
  monthlyDelta: number;
  downDelta: number;
  termDelta: number;
  vehicleModel?: string;
}

const ESSENTIAL_PRODUCT_TYPE = 'Essential';
const APPLIED_STATUS = 'Applied';

type LoanLike = NonNullable<ReviewSummaryResponse['funding']['loan']>;

/**
 * "How much does this product add to the loan?" — current loan minus the
 * what-if loan that excludes the product. Returns zero deltas when either
 * loan is missing (e.g., the API hasn't shipped the baseline yet, or the
 * customer isn't financed).
 */
function deltasBetweenLoans(loan: LoanLike | undefined, loanWithoutProduct: LoanLike | undefined) {
  if (!loan || !loanWithoutProduct) {
    return { monthlyDelta: 0, downDelta: 0, termDelta: 0 };
  }
  return {
    monthlyDelta: loan.payment - loanWithoutProduct.payment,
    downDelta: loan.down - loanWithoutProduct.down,
    termDelta: loan.term - loanWithoutProduct.term
  };
}

/**
 * True when the product moves the down payment or the loan term — the two
 * fields named by the modal's terms-changed strip ("This option adds $X to
 * your down payment and Y months to your loan term."). Monthly-payment
 * differences are NOT counted here, since those are already shown by the
 * "+$X/mo" amount in the cost card and don't need a separate strip.
 */
function loansDiffer(
  loan: LoanLike | undefined,
  loanWithoutProduct: LoanLike | undefined
): boolean {
  if (!loan || !loanWithoutProduct) return false;
  return loan.down !== loanWithoutProduct.down || loan.term !== loanWithoutProduct.term;
}

/**
 * Per-order gate (either product *or* gap Applied — not per-product) with two
 * jobs: it selects the redesigned CC/GAP presentation on review-order, and it is
 * still the enrollment gate for the live pp-review-order-cc-gap-recovery-cio-treatment
 * experiment. Widening it to include Declined/NotSet would both ship copy that was
 * never validated for those customers and change that experiment's population
 * mid-flight.
 */
export function getHasAppliedCoverage(summary: ReviewSummaryResponse | null): boolean {
  return summary?.product?.status === APPLIED_STATUS || summary?.gap?.status === APPLIED_STATUS;
}

export function getCarvanaCareModalVariants(
  summary: ReviewSummaryResponse
): CarvanaCareModalVariants {
  const product = summary?.product;
  // showSocialProof: true unless the customer's selection is Essentials.
  // Missing product (no selection yet) → true (safe default — modal won't render anyway).
  const showSocialProof = product?.type !== ESSENTIAL_PRODUCT_TYPE;
  // Banner copy ("Adding this plan adjusts your monthly payment and term")
  // only makes sense when the product is Applied — otherwise comparing the
  // with-product loan against the without-VSC loan is meaningless.
  const impactsFinancingTerms =
    product?.status === APPLIED_STATUS &&
    loansDiffer(summary?.funding?.loan, summary?.funding?.loanWithoutVsc);

  return {
    showSocialProof,
    impactsFinancingTerms
  };
}

export function getCarvanaCareModalDeltas(summary: ReviewSummaryResponse): CarvanaCareModalDeltas {
  const { monthlyDelta, downDelta, termDelta } = deltasBetweenLoans(
    summary?.funding?.loan,
    summary?.funding?.loanWithoutVsc
  );

  return {
    monthlyDelta,
    downDelta,
    termDelta,
    vehicleModel: summary?.vehicle?.model
  };
}

/**
 * True when the customer's order is financed through Carvana — i.e., we have a
 * Carvana loan to spread costs over. Cash and stand-alone financing (SAF)
 * customers return false; for them the modals show the full product price
 * instead of a monthly delta.
 *
 * Named `getIs…` rather than `is…` to avoid colliding with the
 * `isCarvanaFinanced` boolean that the review-order view models already expose.
 */
export function getIsCarvanaFinanced(summary: ReviewSummaryResponse): boolean {
  return (
    summary?.funding?.type?.toLowerCase() === 'financing' &&
    summary?.funding?.institution?.toLowerCase() === 'carvana'
  );
}

/**
 * Copy for the CC/GAP modal "terms-changed" strip. When adding the product
 * would REDUCE the customer's down payment (negative delta) we swap in the
 * "reduces … by $X" copy per Figma; otherwise we use the "adds …" copy with
 * an optional loan-term clause. The Figma design for the reduces-DP case
 * intentionally omits any term clause even when the loan term also shifts.
 */
export function buildFinancingImpactStripText({
  downDelta,
  termDelta
}: {
  downDelta: number;
  termDelta: number;
}): string {
  const dollars = currency(downDelta, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (downDelta < 0) {
    return `This option reduces your down payment by ${dollars}.`;
  }
  const termClause = termDelta !== 0 ? ` and ${termDelta} months to your loan term` : '';
  return `This option adds ${dollars} to your down payment${termClause}.`;
}

export function getGapModalImpactsFinancingTerms(summary: ReviewSummaryResponse): boolean {
  const gap = summary?.gap;
  return (
    gap?.status === APPLIED_STATUS &&
    loansDiffer(summary?.funding?.loan, summary?.funding?.loanWithoutGap)
  );
}

export function getGapModalProps(summary: ReviewSummaryResponse): GapModalProps {
  const loan = summary?.funding?.loan;
  const { monthlyDelta, downDelta, termDelta } = deltasBetweenLoans(
    loan,
    summary?.funding?.loanWithoutGap
  );

  return {
    impactsFinancingTerms: getGapModalImpactsFinancingTerms(summary),
    termMonths: loan?.term,
    monthlyDelta,
    downDelta,
    termDelta,
    vehicleModel: summary?.vehicle?.model
  };
}
